import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import TranslateIcon from "@mui/icons-material/Translate";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { switchLang } from "../../store/languageSlice";
import { clearUser } from "../../store/userSlice";
import logo from "../../assets/images/logo.png";
const Navbar = () => {
  const { navData, selectedLang } = useSelector((state) => state.language);
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [nav, setNav] = useState(false);
  const [showSelect, setShowSelect] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDark, setIsDark] = useState(
    () => localStorage.getItem("theme") === "dark",
  );
  const langRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setShowSelect(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const changeTheme = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem("theme", next ? "dark" : "light");
    if (next) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  const onSelectClick = (id) => {
    dispatch(switchLang(navData[id - 1]));
    setShowSelect(false);
  };

  const handleLogout = () => {
    dispatch(clearUser());
    
    navigate("/");
  };

  return (
    <>
      {/* ── Navbar bar ─────────────────────────────────── */}
      <div
        className={[
          "fixed top-0 left-0 w-full z-20 h-[72px] flex items-center",
          "bg-white/85 dark:bg-[#030712]/85 backdrop-blur-md",
          "border-b border-green-500/20 transition-all duration-300",
          scrolled
            ? "shadow-[0_4px_30px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_30px_rgba(0,0,0,0.4)]"
            : "",
        ].join(" ")}
      >
        <div className="container">
          <div className="flex items-center justify-between h-[72px] gap-4">
            {/* Brand logo */}
            <Link
              to="/"
              className="flex items-center gap-2.5 no-underline flex-shrink-0"
            >
              <img src={logo} alt="Logo" className="w-[120px]" />
            </Link>

            {/* Desktop right — action buttons ─────────── */}
            <div className="hidden md:flex items-center gap-3">
              {/* Language selector */}
              <div className="relative" ref={langRef}>
                <button
                  className="flex items-center gap-1.5 px-3 py-[6px] rounded-lg border border-black/10 dark:border-white/10 bg-transparent cursor-pointer font-semibold text-[13px] text-black dark:text-white transition-all hover:border-green-500 hover:bg-green-500/[0.06]"
                  onClick={() => setShowSelect(!showSelect)}
                >
                  <TranslateIcon sx={{ fontSize: 16 }} />
                  {selectedLang.name}
                  <span className="text-[10px] opacity-60">▼</span>
                </button>

                {showSelect && (
                  <div className="animate-drop-in absolute top-[calc(100%+8px)] left-0 min-w-[100px] bg-white dark:bg-[#0f172a] rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.5)] border border-black/[0.06] dark:border-white/[0.06] overflow-hidden z-[100]">
                    {navData?.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => onSelectClick(item.id)}
                        className="px-4 py-2.5 cursor-pointer text-[13px] font-semibold text-[#1e293b] dark:text-[#e2e8f0] transition-colors hover:bg-green-500/[0.08] hover:text-green-500"
                      >
                        {item.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Theme toggle pill */}
              <button
                onClick={changeTheme}
                aria-label="Toggle theme"
                className="relative w-[52px] h-[28px] rounded-full border-0 cursor-pointer p-0 bg-slate-200 dark:bg-[#1e293b] transition-colors duration-300 flex-shrink-0"
              >
                <div
                  className={[
                    "absolute top-[3px] left-[3px] w-[22px] h-[22px] rounded-full",
                    "flex items-center justify-center text-[12px] leading-none",
                    "shadow-[0_1px_4px_rgba(0,0,0,0.18)]",
                    "transition-[transform] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
                    isDark
                      ? "translate-x-6 bg-[#0f172a]"
                      : "translate-x-0 bg-white",
                  ].join(" ")}
                >
                  {isDark ? "🌙" : "☀️"}
                </div>
              </button>
              {user?.status === "Active" ? (
                <>
                  <button
                    className="flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-bold cursor-pointer border-0 tracking-[0.3px] whitespace-nowrap bg-gradient-to-br from-green-400 to-green-600 text-white shadow-[0_3px_12px_rgba(34,197,94,0.3)] hover:shadow-[0_5px_18px_rgba(34,197,94,0.45)] hover:-translate-y-px transition-all"
                    onClick={() => navigate("/dashboard")}
                  >
                    <DashboardIcon sx={{ fontSize: 16 }} />
                    {t("navbar.dashboard")}
                  </button>
                  <button
                    className="flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-bold cursor-pointer tracking-[0.3px] whitespace-nowrap bg-transparent text-green-500 border border-green-500 hover:bg-green-500/[0.06] hover:-translate-y-px transition-all"
                    onClick={handleLogout}
                  >
                    <LogoutIcon sx={{ fontSize: 16 }} />
                    {t("navbar.logout")}
                  </button>
                </>
              ) : (
                <Link to="/login">
                  <button className="flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-bold cursor-pointer border-0 tracking-[0.3px] whitespace-nowrap bg-gradient-to-br from-green-400 to-green-600 text-white shadow-[0_3px_12px_rgba(34,197,94,0.3)] hover:shadow-[0_5px_18px_rgba(34,197,94,0.45)] hover:-translate-y-px transition-all">
                    <LoginIcon sx={{ fontSize: 16 }} />
                    {t("navbar.login")}
                  </button>
                </Link>
              )}
            </div>

            {/* Mobile hamburger — hidden md+ via Tailwind */}
            <button
              className={[
                "flex md:hidden items-center justify-center w-9 h-9 rounded-lg",
                "border border-black/10 dark:border-white/10 bg-transparent cursor-pointer",
                "transition-all text-[#1e293b] dark:text-[#e2e8f0]",
                "hover:border-green-500 hover:text-green-500",
              ].join(" ")}
              onClick={() => setNav(!nav)}
            >
              {nav ? (
                <CloseIcon sx={{ fontSize: 20 }} />
              ) : (
                <MenuIcon sx={{ fontSize: 20 }} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile overlay ─────────────────────────────── */}
      {nav && (
        <div className="animate-slide-down fixed top-[72px] left-0 w-full h-[calc(100vh-72px)] bg-white/[0.97] dark:bg-[#030712]/[0.97] backdrop-blur-xl z-[19] flex flex-col items-center justify-center gap-5 md:hidden">
          {/* Language + Theme row */}
          <div className="flex items-center gap-4">
            <div className="relative" ref={langRef}>
              <button
                className="flex items-center gap-1.5 px-3 py-[6px] rounded-lg border border-black/10 dark:border-white/10 bg-transparent cursor-pointer font-semibold text-[13px] text-black dark:text-white transition-all hover:border-green-500 hover:bg-green-500/[0.06]"
                onClick={() => setShowSelect(!showSelect)}
              >
                <TranslateIcon sx={{ fontSize: 16 }} />
                {selectedLang.name}
                <span className="text-[10px] opacity-60">▼</span>
              </button>
              {showSelect && (
                <div className="animate-drop-in absolute top-[calc(100%+8px)] left-0 min-w-[100px] bg-white dark:bg-[#0f172a] rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.5)] border border-black/[0.06] dark:border-white/[0.06] overflow-hidden z-[100]">
                  {navData?.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => onSelectClick(item.id)}
                      className="px-4 py-2.5 cursor-pointer text-[13px] font-semibold text-[#1e293b] dark:text-[#e2e8f0] transition-colors hover:bg-green-500/[0.08] hover:text-green-500"
                    >
                      {item.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Theme toggle pill */}
            <button
              onClick={changeTheme}
              aria-label="Toggle theme"
              className="relative w-[52px] h-[28px] rounded-full border-0 cursor-pointer p-0 bg-slate-200 dark:bg-[#1e293b] transition-colors duration-300 flex-shrink-0"
            >
              <div
                className={[
                  "absolute top-[3px] left-[3px] w-[22px] h-[22px] rounded-full",
                  "flex items-center justify-center text-[12px] leading-none",
                  "shadow-[0_1px_4px_rgba(0,0,0,0.18)]",
                  "transition-[transform] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
                  isDark
                    ? "translate-x-6 bg-[#0f172a]"
                    : "translate-x-0 bg-white",
                ].join(" ")}
              >
                {isDark ? "🌙" : "☀️"}
              </div>
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col items-center gap-3 w-4/5 max-w-[320px]">
            {user?.status === "Active" ? (
              <>
                <button
                  className="flex items-center justify-center gap-1.5 w-full px-4 py-2 rounded-[9px] text-[13px] font-bold cursor-pointer border-0 tracking-[0.3px] bg-gradient-to-br from-green-400 to-green-600 text-white shadow-[0_3px_12px_rgba(34,197,94,0.3)] hover:shadow-[0_5px_18px_rgba(34,197,94,0.45)] transition-all"
                  onClick={() => {
                    navigate("/dashboard");
                    setNav(false);
                  }}
                >
                  <DashboardIcon sx={{ fontSize: 16 }} />
                  {t("navbar.dashboard")}
                </button>
                <button
                  className="flex items-center justify-center gap-1.5 w-full px-4 py-2 rounded-[9px] text-[13px] font-bold cursor-pointer tracking-[0.3px] bg-transparent text-green-500 border border-green-500 hover:bg-green-500/[0.06] transition-all"
                  onClick={() => {
                    handleLogout();
                    setNav(false);
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 16 }} />
                  {t("navbar.logout")}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="w-full"
                onClick={() => setNav(false)}
              >
                <button className="flex items-center justify-center gap-1.5 w-full px-4 py-2 rounded-[9px] text-[13px] font-bold cursor-pointer border-0 tracking-[0.3px] bg-gradient-to-br from-green-400 to-green-600 text-white shadow-[0_3px_12px_rgba(34,197,94,0.3)] hover:shadow-[0_5px_18px_rgba(34,197,94,0.45)] transition-all">
                  <LoginIcon sx={{ fontSize: 16 }} />
                  {t("navbar.login")}
                </button>
              </Link>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
